"use client";

import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslations } from "next-intl";
import { Link } from "@/lib/i18n/navigation";
import { toast } from "sonner";

type Props = {
  physicianSlug: string;
  physicianName: string;
  ariaLabel: string;
  className?: string;
};

/**
 * Bouton favori (étoile) du hero médecin.
 * - Connecté : toggle l'ajout/retrait via /api/favorites (POST), avec toast feedback.
 * - Non connecté : ouvre un dialog "Créer un compte ou se connecter pour ajouter en favoris".
 */
export function FavoriteButton({ physicianSlug, physicianName, ariaLabel, className }: Props) {
  const t = useTranslations("physician.favorite");
  const [isFavorited, setIsFavorited] = useState(false);
  const [pending, setPending] = useState(false);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);

  // Charge l'état initial (favori ou non) au montage si l'utilisateur est connecté.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/favorites", { credentials: "include" });
        if (!res.ok) return; // 401 = non connecté, on reste à false
        const data = await res.json();
        if (cancelled) return;
        const list = (data?.favorites ?? []) as Array<{ physician?: { slug?: string } }>;
        setIsFavorited(list.some((f) => f.physician?.slug === physicianSlug));
      } catch {
        /* silent */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [physicianSlug]);

  async function handleClick() {
    if (pending) return;
    setPending(true);
    try {
      const res = await fetch("/api/favorites", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ physicianSlug }),
      });
      if (res.status === 401) {
        setAuthDialogOpen(true);
        return;
      }
      const data = await res.json();
      if (data?.success) {
        setIsFavorited(Boolean(data.favorited));
        toast.success(
          data.favorited
            ? t("addedToast", { name: physicianName })
            : t("removedToast", { name: physicianName }),
        );
      } else {
        toast.error(t("errorToast"));
      }
    } catch {
      toast.error(t("errorToast"));
    } finally {
      setPending(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={pending}
        aria-label={ariaLabel}
        aria-pressed={isFavorited}
        className={
          className ??
          "inline-flex h-7 w-7 shrink-0 items-center justify-center text-white/70 transition-colors hover:text-white disabled:opacity-50"
        }
      >
        <svg
          viewBox="0 0 24 24"
          className="h-5 w-5"
          fill={isFavorited ? "currentColor" : "none"}
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden
        >
          <path
            d="m12 17.27 6.18 3.73-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <Dialog.Root open={authDialogOpen} onOpenChange={setAuthDialogOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-[80] bg-[rgb(15_30_58_/_0.45)] backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:duration-150" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-[90] w-[calc(100%-2rem)] max-w-[340px] -translate-x-1/2 -translate-y-1/2 rounded-[20px] bg-white p-5 shadow-[0_20px_50px_-12px_rgba(15,30,58,0.35)] outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=open]:slide-in-from-bottom-2 data-[state=open]:duration-200">
            <div className="text-center">
              <span className="relative inline-flex h-11 w-11 items-center justify-center">
                <span aria-hidden className="absolute inset-0 rounded-full bg-gradient-to-br from-[var(--sahha-accent-100)] to-[var(--sahha-primary-100)]" />
                <svg viewBox="0 0 24 24" className="relative h-5 w-5 text-[var(--sahha-accent-500)]" fill="currentColor" aria-hidden>
                  <path
                    d="m12 17.27 6.18 3.73-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <Dialog.Title className="mt-3 text-[15.5px] font-extrabold leading-tight text-[var(--sahha-text-900)]">
                {t("authDialogTitle")}
              </Dialog.Title>
              <Dialog.Description className="mt-1.5 text-[12.5px] leading-snug text-[var(--sahha-text-600)]">
                {t("authDialogBody")}
              </Dialog.Description>
            </div>

            <div className="mt-4 flex flex-col gap-2.5">
              <Link
                href={`/connexion?next=/medecin/${physicianSlug}` as never}
                className="flex h-11 w-full items-center justify-center rounded-xl bg-[var(--sahha-primary-700)] text-[13.5px] font-bold text-white shadow-[0_4px_12px_-4px_rgba(14,116,144,0.45)] transition-all hover:bg-[var(--sahha-primary-800)] hover:shadow-[0_6px_16px_-4px_rgba(14,116,144,0.55)] active:scale-[0.98]"
                onClick={() => setAuthDialogOpen(false)}
              >
                {t("authDialogLogin")}
              </Link>
              <Link
                href={`/inscription?next=/medecin/${physicianSlug}` as never}
                className="flex h-11 w-full items-center justify-center rounded-xl bg-white text-[13.5px] font-bold text-[var(--sahha-primary-700)] ring-1.5 ring-[var(--sahha-primary-200)] transition-all hover:bg-[var(--sahha-primary-50)] hover:ring-[var(--sahha-primary-300)] active:scale-[0.98]"
                style={{ boxShadow: "inset 0 0 0 1.5px var(--sahha-primary-200)" }}
                onClick={() => setAuthDialogOpen(false)}
              >
                {t("authDialogSignup")}
              </Link>
            </div>

            <Dialog.Close asChild>
              <button
                type="button"
                aria-label={t("authDialogClose")}
                className="absolute right-3 top-3 inline-flex h-7 w-7 items-center justify-center rounded-full text-[var(--sahha-text-400)] transition hover:bg-[var(--sahha-surface-1)] hover:text-[var(--sahha-text-700)]"
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </Dialog.Close>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}
